import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { BackdropCircular } from 'components';
import {
  createActiveCartCombinedAPI,
  openCheckoutServiceApi,
  publishPushCartTreatmentsApi,
  createDraftQuoteApi
} from 'api';
import * as yup from 'yup';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { addDays } from 'date-fns';
import NumberFormat from 'react-number-format';
import { Formik } from 'formik';
import { useStyles } from '../styles';


const QuoteTreatmentPlanButtonsGroup = ({ totals }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{id?: string}>();
  const [openDialog, setOpenDialog] = useState(false);

  const {
    quotePlanSelectedSeriesIds,
    quotePlan,
    currentQuoteCustomer,
    customerPushCart,
    loadingActiveMagentoCart,
    loadingCheckout,
    loadingCustomerPushCart,
    loadingDraftedQuotes
  } = useSelector((state: IReduxState) => state.quote);

  const loading =
    loadingActiveMagentoCart ||
    loadingCheckout ||
    loadingCustomerPushCart ||
    loadingDraftedQuotes;

  const selectedSeries = quotePlan?.find((series) => series._id === quotePlanSelectedSeriesIds[0]);

  const handleCreateSelectedQuotes = async () => {
    const selectedSeries = quotePlan.filter((quote) => quotePlanSelectedSeriesIds.includes(quote._id));

    await publishPushCartTreatmentsApi({
      dispatch,
      pushCartId: customerPushCart._id,
      publishData: { selectedTreatments: selectedSeries }
    });

    if (!customerPushCart) {
      history.push(`/quoting/profile/${id}`);
    }
  };

  const handleProceedToPayNow = async (quoteData) => {
    setOpenDialog(true);
    //pay now available only when one series selected

    const selectedItems = selectedSeries.selectedItems.map(({sku, qty, product_option, price}) => ({
      sku,
      qty,
      price,
      product_option
    }));

    const activeCartId = await createActiveCartCombinedAPI({
      dispatch,
      customerId: id,
      selectedItems,
      quoteDiscount: {
        price_type: quoteData?.price_type || selectedSeries?.quoteDiscount?.price_type || '',
        price: quoteData?.price || selectedSeries?.quoteDiscount?.price || 0
      }
    });

    if (activeCartId) {
      openCheckoutServiceApi({
        dispatch,
        email: currentQuoteCustomer.email,
        pushCartId: customerPushCart._id
      });
    }
  };

  const isQuoteDatesPast = (quote) => {
    const currentDate = new Date();
    const {startDate, expirationDate, mustUsedDate} = quote;

    return addDays(new Date(startDate), 1) < currentDate || new Date(expirationDate) < currentDate || new Date(mustUsedDate) < currentDate;
  };

  const transformQuoteData = (quote) => ({
    isTreatment: true,
    ...quote
  });

  const handleSaveQuote = async () => {
    const filteredQuotes = quotePlan.filter((quote) => quotePlanSelectedSeriesIds.includes(quote._id));

    for await (const quote of filteredQuotes) {
      const transformedData = transformQuoteData(quote);
      await createDraftQuoteApi({
        dispatch,
        pushCartId: customerPushCart._id,
        pushCart: transformedData
      });
    }
  };

  const isDatePast = quotePlan.some((quote) => quotePlanSelectedSeriesIds.includes(quote._id) && isQuoteDatesPast(quote));

  return (
    <div className="mb-2">
      <Button
        fullWidth
        className={`${classes.customQuoteSummaryButton} mb-4`}
        variant="contained"
        color="primary"
        size="large"
        disabled={!quotePlanSelectedSeriesIds?.length || isDatePast}
        onClick={handleCreateSelectedQuotes}
      >
        Create a Quote
      </Button>
      <Button
        className={`${classes.customQuoteSummaryButton} mb-4`}
        variant="contained"
        color="primary"
        size="large"
        disabled={!quotePlanSelectedSeriesIds?.length || isDatePast}
        fullWidth
        onClick={handleSaveQuote}
      >
        Save Quote
      </Button>
      <Button
        fullWidth
        className={`${classes.customQuoteSummaryButton} mb-4`}
        variant="contained"
        color="primary"
        size="large"
        disabled={quotePlanSelectedSeriesIds?.length !== 1 || isDatePast}
        onClick={() => setOpenDialog(true)}
      >
        Pay Now
      </Button>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="quote-notes-dialog"
        PaperProps={{
          className: 'border-radius-6 p-3'
        }}
        fullWidth
        maxWidth={'sm'}
      >
        <DialogTitle id="quote-notes-dialog" className="flex-column w-full p-3">
          <div className="flex justify-between">
            <Typography variant="h5" className={`${classes.modalTitle} mb-0`}>
              Quote Attributes
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenDialog(false)}
            >
              <Icon fontSize="small">close</Icon>
            </IconButton>
          </div>
        </DialogTitle>
        <Formik
          initialValues={{
            price_type: selectedSeries?.quoteDiscount?.price_type || '',
            price: selectedSeries?.quoteDiscount?.price || 0
          }}
          validationSchema={
            yup.object({
              price_type: yup.string(),
              price: yup
                .number()
                .typeError('Allowed only numerical symbols')
                .positive('Value must be positive')
                .min(0, 'Price should be a positive number')
                .test(
                  'price',
                  'The unit price can\'t be lower than discount',
                  function (value) {
                    const { price_type } = this.parent;
                    const formattedFixedPrice = totals.subtotal?.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    });

                    if (price_type === 'percent') {
                      if (isNaN(value) || value < 0 || value > 100) {
                        return this.createError({ message: 'Discount must be between 0 and 100' });
                      }
                    } else if (isNaN(value) || value < 0 || value > totals.subtotal) {
                      return this.createError({
                        message: `Discount must be between 0 and $${formattedFixedPrice}`,
                      });
                    }

                    return true;
                  }
                )
                .required('Field is required'),
            })
          }
          onSubmit={(values, { resetForm }) => {
            handleProceedToPayNow(values);
            setOpenDialog(false);
            resetForm();
          }}
        >
          {({
            values: {
              price_type,
              price
            },
            errors,
            touched,
            isValid,
            handleBlur,
            handleSubmit,
            setFieldValue,
            handleChange
          }) => (
            <form onSubmit={handleSubmit} noValidate className="w-full">
              <DialogContent className="p-3">
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box className="w-full">
                      <TextField
                        label="Discount Type"
                        select
                        variant="outlined"
                        name="price_type"
                        fullWidth
                        value={price_type}
                        onChange={(event) => {
                          const selectedValue = event.target.value;
                          const newPrice = selectedValue === '' ? 0 : price;
                          setFieldValue('price_type', selectedValue);
                          setFieldValue('price', newPrice);
                        }}
                        onBlur={handleBlur}
                        error={touched.price_type && Boolean(errors.price_type)}
                        helperText={touched.price_type && errors.price_type}
                      >
                        <MenuItem value="percent">Percentage</MenuItem>
                        <MenuItem value="fixed">Fixed</MenuItem>
                        <MenuItem value="">Not selected</MenuItem>
                      </TextField>
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Box className="w-full">
                      <NumberFormat
                        customInput={TextField}
                        variant="outlined"
                        name="price"
                        fullWidth
                        label="Discount Value"
                        value={price}
                        onValueChange={(values) => {
                          const { floatValue } = values;
                          handleChange({
                            target: {
                              name: 'price',
                              value: floatValue !== undefined ? floatValue : ''
                            },
                          });
                        }}
                        onBlur={handleBlur}
                        disabled={!price_type}
                        suffix={price_type === 'percent' ? '%' : ''}
                        prefix={price_type === 'fixed' ? '$' : ''}
                        fixedDecimalScale
                        decimalScale={2}
                        error={touched.price && Boolean(errors.price)}
                        helperText={touched.price && errors.price}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions className="justify-between">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setOpenDialog(false)}
                  className="w-120 uppercase"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!isValid}
                  className="uppercase"
                >
                  Pay Now
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default QuoteTreatmentPlanButtonsGroup;
